import {
  CARE_WATCH_STATUSES,
  CARE_WATCH_VIEWS,
  CATEGORY_OPTIONS,
  DOC_ISSUE_LABELS,
  DOC_ISSUES,
  KPI_ISSUE_BY_KEY,
  LIVE_KPI_KEYS,
  LIVE_STATES,
  RISK_LEVELS,
  isCareWatchLiveView,
} from "./constants";

/** Every paperwork gap on one timesheet. Older payloads only send `issue`. */
export const documentationIssuesOf = (item) => {
  if (Array.isArray(item?.issues) && item.issues.length) return item.issues;
  return item?.issue ? [item.issue] : [];
};

export const hasDocumentationIssue = (item, issue) =>
  documentationIssuesOf(item).includes(issue);

const uniqueLabels = (labels) => {
  const unique = [];
  (Array.isArray(labels) ? labels : []).forEach((label) => {
    const text = String(label || "").trim();
    if (!text || unique.includes(text)) return;
    unique.push(text);
  });
  return unique;
};

/** First label stays visible. The rest collapse to a +N tooltip. */
export const labelOverflow = (labels) => {
  const unique = uniqueLabels(labels);
  return {
    visible: unique[0] || "",
    overflow: unique.slice(1),
  };
};

export const documentationIssueOverflow = (item) =>
  labelOverflow(
    documentationIssuesOf(item).map((issue) => DOC_ISSUE_LABELS[issue] || "")
  );

/**
 * Analyser line keeps the headline finding. Other check names on the same
 * missed-documentation timesheet are the +N tooltip.
 */
export const analyserOverflowLabels = (analyser, checks) => {
  const primary = String(analyser || "").trim();
  const rest = uniqueLabels(
    (Array.isArray(checks) ? checks : []).map((check) => check?.label)
  ).filter((label) => label !== primary);

  if (!primary && rest.length) {
    return { visible: rest[0], overflow: rest.slice(1) };
  }

  return { visible: primary, overflow: rest };
};

export const matchesSearch = (item, search, view) => {
  const q = (search || "").trim().toLowerCase();
  if (!q) return true;

  const names = (item.participants || [])
    .map((person) =>
      typeof person === "string"
        ? person
        : `${person.firstName || person.first_name || ""} ${
            person.lastName || person.last_name || ""
          }`
    )
    .join(" ");

  const workerName =
    typeof item.worker === "string"
      ? item.worker
      : `${item.workerRef?.firstName || item.worker?.firstName || ""} ${
          item.workerRef?.lastName || item.worker?.lastName || ""
        }`;

  if (isCareWatchLiveView(view || item.view)) {
    return [names, workerName].filter(Boolean).join(" ").toLowerCase().includes(q);
  }

  if ((view || item.view) === CARE_WATCH_VIEWS.DOCUMENTATION) {
    const issue = documentationIssuesOf(item).join(" ");
    const issueLabel = documentationIssuesOf(item)
      .map((key) => DOC_ISSUE_LABELS[key] || "")
      .filter(Boolean)
      .join(" ");
    return [
      names,
      workerName,
      item.preview,
      item.note,
      item.analyser,
      issue,
      issue.replace(/_/g, " "),
      issueLabel,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q);
  }

  const categoryOptionLabel =
    CATEGORY_OPTIONS.find((option) => option.value === item.category)?.label || "";

  return [
    names,
    workerName,
    item.preview,
    item.note,
    item.categoryLabel,
    ...(Array.isArray(item.categoryLabels) ? item.categoryLabels : []),
    item.category,
    String(item.category || "").replace(/_/g, " "),
    categoryOptionLabel,
    item.analyser,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(q);
};

export const matchesStatus = (item, status) => {
  if (status === CARE_WATCH_STATUSES.ALL) return true;
  if (status === CARE_WATCH_STATUSES.OVERDUE) return Boolean(item.overdue);
  return item.status === status;
};

const matchesLiveState = (item, liveState) => {
  const selected = liveState || LIVE_STATES.ALL;
  if (selected === LIVE_STATES.ALL || selected === LIVE_STATES.ON_FLOOR) {
    return true;
  }
  return item.liveState === selected;
};

/**
 * Calendar day as YYYY-MM-DD.
 *
 * The list sends shiftDate as a timestamp ("2026-09-21 00:00:00"). The date
 * filter is a bare day. Comparing those strings makes the end day exclusive:
 * "2026-09-21 00:00:00" > "2026-09-21", so a range that starts and ends on
 * the same day drops every row the API just returned.
 */
export const calendarDate = (value) => {
  if (value == null || value === "") return "";
  const datePart = String(value).trim().split(/[T ]/)[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : "";
};

/** Empty means every category. A lone "all" from older state means the same. */
export const selectedCategories = (category) => {
  const values = Array.isArray(category) ? category : [category];
  return values.filter((value) => value && value !== "all");
};

const matchesDateRange = (item, filters) => {
  const shiftDay = calendarDate(item.shiftDate);
  if (!shiftDay) return true;
  const from = calendarDate(filters.fromDate);
  const to = calendarDate(filters.toDate);
  if (from && shiftDay < from) return false;
  if (to && shiftDay > to) return false;
  return true;
};

export const filterSignals = (items, filters, activeKpi) => {
  return (items || []).filter((item) => {
    if (item.view !== filters.view) return false;
    if (isCareWatchLiveView(filters.view)) {
      if (!matchesSearch(item, filters.search, filters.view)) return false;
      return matchesLiveState(item, filters.liveState);
    }
    if (!matchesSearch(item, filters.search, filters.view)) return false;
    if (!matchesStatus(item, filters.status)) return false;
    if (!matchesDateRange(item, filters)) return false;
    const categories = Array.isArray(item.categories)
      ? item.categories
      : item.category
        ? [item.category]
        : [];
    const chosenCategories = selectedCategories(filters.category);
    if (
      filters.view === CARE_WATCH_VIEWS.INCIDENT &&
      chosenCategories.length > 0 &&
      !chosenCategories.some((category) => categories.includes(category))
    ) {
      return false;
    }
    if (filters.risk && filters.risk !== "all" && item.risk !== filters.risk) {
      return false;
    }
    if (
      filters.issue &&
      filters.issue !== "all" &&
      !hasDocumentationIssue(item, filters.issue)
    ) {
      return false;
    }
    if (activeKpi === "high") return item.risk === RISK_LEVELS.HIGH;
    if (activeKpi === "moderate") return item.risk === RISK_LEVELS.MODERATE;
    if (activeKpi === "low") return item.risk === RISK_LEVELS.LOW;
    if (activeKpi === "overdue") return Boolean(item.overdue);
    if (activeKpi === "promoted") {
      return item.status === CARE_WATCH_STATUSES.PROMOTED;
    }
    if (KPI_ISSUE_BY_KEY[activeKpi]) {
      return hasDocumentationIssue(item, KPI_ISSUE_BY_KEY[activeKpi]);
    }
    return true;
  });
};

/**
 * Whether a KPI tile should render as selected.
 *
 * The three filter dimensions (risk, status-override, doc-issue) are mutually
 * exclusive for tile highlighting — at most one dimension is "the KPI
 * selection". When two dimensions happen to be non-default (e.g. from manual
 * dropdown use), no tile highlights because we cannot decide which one the
 * user "meant" to select via the tiles.
 */
export const isCareWatchKpiActive = (key, filters = {}) => {
  if (LIVE_KPI_KEYS.includes(key)) {
    const current = filters.liveState || LIVE_STATES.ALL;
    return current === key;
  }

  const hasRisk = Boolean(filters.risk) && filters.risk !== "all";
  const hasStatusOverride =
    filters.status === "overdue" || filters.status === "promoted";
  const hasIssue = Boolean(filters.issue) && filters.issue !== "all";

  const activeDimensions =
    (hasRisk ? 1 : 0) + (hasStatusOverride ? 1 : 0) + (hasIssue ? 1 : 0);
  if (activeDimensions !== 1) return false;

  if (
    key === RISK_LEVELS.HIGH ||
    key === RISK_LEVELS.MODERATE ||
    key === RISK_LEVELS.LOW
  ) {
    return filters.risk === key;
  }
  if (key === "overdue" || key === "promoted") {
    return filters.status === key;
  }
  const issue = KPI_ISSUE_BY_KEY[key];
  return Boolean(issue) && filters.issue === issue;
};

/**
 * Filter updates for a KPI tile click. Returns an array of `{key, value}`
 * pairs so that clicking one tile clears the other dimensions — risk, status
 * (overdue/promoted), and doc-issue tiles are mutually exclusive.
 *
 * Clicking the already-active tile toggles it off (risk/issue → "all",
 * status → the queue default "needs_follow_up").
 */
export const kpiClickFilterUpdate = (key, filters = {}) => {
  const updates = [];

  if (LIVE_KPI_KEYS.includes(key)) {
    const current = filters.liveState || LIVE_STATES.ALL;
    updates.push({
      key: "liveState",
      value: current === key ? LIVE_STATES.ALL : key,
    });
    return updates;
  }

  if (
    key === RISK_LEVELS.HIGH ||
    key === RISK_LEVELS.MODERATE ||
    key === RISK_LEVELS.LOW
  ) {
    const toggling = filters.risk === key;
    updates.push({ key: "risk", value: toggling ? "all" : key });
    if (filters.issue && filters.issue !== "all") {
      updates.push({ key: "issue", value: "all" });
    }
    if (filters.status === "overdue" || filters.status === "promoted") {
      updates.push({ key: "status", value: CARE_WATCH_STATUSES.NEEDS_FOLLOW_UP });
    }
    return updates;
  }

  if (key === "overdue" || key === "promoted") {
    const toggling = filters.status === key;
    updates.push({
      key: "status",
      value: toggling ? CARE_WATCH_STATUSES.NEEDS_FOLLOW_UP : key,
    });
    if (filters.risk && filters.risk !== "all") {
      updates.push({ key: "risk", value: "all" });
    }
    if (filters.issue && filters.issue !== "all") {
      updates.push({ key: "issue", value: "all" });
    }
    return updates;
  }

  const issue = KPI_ISSUE_BY_KEY[key];
  if (issue) {
    const toggling = filters.issue === issue;
    updates.push({ key: "issue", value: toggling ? "all" : issue });
    if (filters.risk && filters.risk !== "all") {
      updates.push({ key: "risk", value: "all" });
    }
    if (filters.status === "overdue" || filters.status === "promoted") {
      updates.push({ key: "status", value: CARE_WATCH_STATUSES.NEEDS_FOLLOW_UP });
    }
    return updates;
  }

  return updates;
};

/**
 * KPI tiles from the server's counts.
 *
 * The counts cover the current tab and status (the same query as the table),
 * not one paginated page. Tile shape is unchanged.
 */
export const buildKpisFromOverview = (overview = {}, view) => {
  const n = (key) => Number(overview?.[key] ?? 0);

  if (isCareWatchLiveView(view)) {
    return [
      { key: LIVE_STATES.IN_PROGRESS, label: "In progress", value: n("in_progress"),
        unit: "shifts", caption: "Clocked in · not yet clocked out",
        accent: "#047857", icon: "clock" },
      { key: LIVE_STATES.NOT_STARTED, label: "Not started", value: n("not_started"),
        unit: "shifts", caption: "No clock-in · under 15 min late",
        accent: "#B45309", icon: "alert-circle" },
      { key: LIVE_STATES.RUNNING_LATE, label: "Running late", value: n("running_late"),
        unit: "shifts", caption: "No clock-in · 15+ min late",
        accent: "#BE123C", icon: "alert-triangle" },
      { key: LIVE_STATES.ON_FLOOR, label: "On the floor",
        value: n("on_floor"),
        unit: "live", caption: "In progress + not started + late",
        accent: "#4B6CB7", icon: "users" },
    ];
  }

  if (view === CARE_WATCH_VIEWS.DOCUMENTATION) {
    return [
      { key: "brief", label: "Shift note", value: n("brief_note"),
        unit: "timesheets", caption: "Generic or under-length notes",
        accent: "#B45309", icon: "file-text" },
      { key: "goals", label: "Goals", value: n("goals_missing"),
        unit: "timesheets", caption: "Goals not referenced",
        accent: "#047857", icon: "target" },
      // The analysis also checks tasks, signatures and end-of-shift questions.
      // These carried no tile, so those gaps were counted into the queue but
      // invisible in the summary above it.
      { key: "tasks", label: "Tasks", value: n("tasks_incomplete"),
        unit: "timesheets", caption: "Shift tasks not completed",
        accent: "#7C3AED", icon: "target" },
      { key: "signature", label: "Signature", value: n("missing_signature"),
        unit: "timesheets", caption: "Signature or attachment missing",
        accent: "#0369A1", icon: "file-text" },
      { key: "endQuestions", label: "End questions", value: n("end_questions_incomplete"),
        unit: "timesheets", caption: "Shift end questions unanswered",
        accent: "#B45309", icon: "info" },
      { key: "overdue", label: "Overdue", value: n("overdue"),
        unit: "follow-up", caption: "Past the 48 hour SLA",
        accent: "#92400E", icon: "clock" },
    ];
  }

  return [
    { key: "high", label: "High", value: n("high"),
      unit: "timesheet", caption: "High severity in this view",
      accent: "#BE123C", icon: "alert-triangle" },
    { key: "moderate", label: "Moderate", value: n("moderate"),
      unit: "timesheet", caption: "Moderate severity in this view",
      accent: "#B45309", icon: "alert-circle" },
    { key: "low", label: "Low", value: n("low"),
      unit: "timesheet", caption: "Low severity in this view",
      accent: "#047857", icon: "info" },
    { key: "overdue", label: "Overdue", value: n("overdue"),
      unit: "follow-up", caption: "Past the 48 hour SLA",
      accent: "#92400E", icon: "clock" },
    { key: "promoted", label: "Created", value: n("promoted"),
      unit: "incident", caption: "Incident created from this timesheet",
      accent: "#4338CA", icon: "share" },
  ];
};

export const computeKpis = (items, view) => {
  const viewItems = (items || []).filter((item) => item.view === view);

  if (view === CARE_WATCH_VIEWS.DOCUMENTATION) {
    return [
      {
        key: "brief",
        label: "Shift note",
        value: viewItems.filter((i) =>
          hasDocumentationIssue(i, DOC_ISSUES.BRIEF_NOTE)
        ).length,
        unit: "timesheets",
        caption: "Generic or under-length notes",
        accent: "#B45309",
        icon: "file-text",
      },
      {
        key: "goals",
        label: "Goals",
        value: viewItems.filter((i) =>
          hasDocumentationIssue(i, DOC_ISSUES.GOALS_MISSING)
        ).length,
        unit: "timesheets",
        caption: "Goals not referenced",
        accent: "#047857",
        icon: "target",
      },
      {
        key: "overdue",
        label: "Overdue",
        value: viewItems.filter((i) => i.overdue).length,
        unit: "follow-up",
        caption: "Past the 48 hour SLA",
        accent: "#92400E",
        icon: "clock",
      },
    ];
  }

  return [
    {
      key: "high",
      label: "High",
      value: viewItems.filter((i) => i.risk === RISK_LEVELS.HIGH).length,
      unit: "timesheet",
      caption: "High severity in this view",
      accent: "#BE123C",
      icon: "alert-triangle",
    },
    {
      key: "moderate",
      label: "Moderate",
      value: viewItems.filter((i) => i.risk === RISK_LEVELS.MODERATE).length,
      unit: "timesheet",
      caption: "Moderate severity in this view",
      accent: "#B45309",
      icon: "alert-circle",
    },
    {
      key: "low",
      label: "Low",
      value: viewItems.filter((i) => i.risk === RISK_LEVELS.LOW).length,
      unit: "timesheet",
      caption: "Low severity in this view",
      accent: "#047857",
      icon: "info",
    },
    {
      key: "overdue",
      label: "Overdue",
      value: viewItems.filter((i) => i.overdue).length,
      unit: "follow-up",
      caption: "Past the 48 hour SLA",
      accent: "#92400E",
      icon: "clock",
    },
    {
      key: "promoted",
      label: "Created",
      value: viewItems.filter((i) => i.status === CARE_WATCH_STATUSES.PROMOTED)
        .length,
      unit: "incident",
      caption: "Incident created from this timesheet",
      accent: "#4B6CB7",
      icon: "file-plus",
    },
  ];
};

export const paginateSignals = (items, page, pageSize) => {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  return {
    total,
    totalPages,
    page: safePage,
    pageSize,
    pageItems: items.slice((safePage - 1) * pageSize, safePage * pageSize),
  };
};
